import { HelpButton } from "../features/help/HelpPanel";
import { MotionEditor } from "./remotion-motion";
import { normalizeTemplateMotion } from "@nodes/remotion-core/motion";
import { useEffect, useState } from "react";
import { forgetAssets } from "../api/library";
import { listProjects } from "../api/workspace";
import type { PageContext } from "../lib/types";
import "./remotion.css";
async function api(path: string, body?: any, method = body ? "POST" : "GET") {
  const r = await fetch("/api/v1/remotion/" + path, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await r.json();
  if (!r.ok) throw new Error(data.error || "Request failed");
  return data;
}
function Preview({ html }: { html: string }) {
  const source = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data: 'self'; style-src 'unsafe-inline'; font-src data:"><style>body{margin:0;width:1080px;height:1350px;transform:scale(.2222);transform-origin:top left}</style>${html}`;
  return (
    <iframe
      className="remotion-preview"
      title="Card preview"
      sandbox="allow-same-origin"
      srcDoc={source}
    />
  );
}
function RemotionTemplates({ context }: { context: PageContext }) {
  const assetEditor = context.path.startsWith('/assets/templates/');
  const templateId = context.params.templateId;
  const projects = listProjects();
  const [projectId, setProject] = useState(context.query.projectId || projects[0]?.id || "");
  const [templates, setTemplates] = useState<any[]>([]);
  const [editor, setEditor] = useState<any>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!projectId && projects[0]?.id) setProject(projects[0].id);
  }, [projectId, projects[0]?.id]);
  const load = async () => {
    if (!projectId && !templateId) return;
    const rows = await api("templates" + (templateId ? '' : '?projectId=' + projectId));
    setTemplates(rows);
    if (assetEditor && templateId) {
      const template = rows.find((row: any) => row.id === templateId);
      if (!template) throw new Error('Template not found or unavailable.');
      setProject(template.project_id);
      setEditor({ ...normalizeTemplateMotion(template.data), id: template.id,
        analysis: { slides: template.data.slides, components: template.data.components } });
    }
  };
  useEffect(() => {
    setEditor(null);
    void load().catch((e) => setError(e.message));
  }, [projectId, templateId]);
  useEffect(() => {
    if (assetEditor && !templateId) newTemplate();
  }, [assetEditor, templateId, projectId]);
  const action = async (fn: () => Promise<any>) => {
    setError("");
    setBusy(true);
    try {
      return await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const newTemplate = () =>
    setEditor({
      name: "New template",
      description: "",
      html: '<article style="width:1080px;height:1350px;background:#ffffff;padding:80px;box-sizing:border-box"><h1 style="font-size:72px">Your headline</h1><p style="font-size:36px">Your text</p></article>',
      bindings: [],
      analysis: null,
      motionPreset: "none",
    });
  const analyze = () =>
    action(async () => {
      const analysis = await api("analyze", { html: editor.html });
      setEditor({
        ...editor,
        html: analysis.html,
        analysis,
        animations: editor.animations,
        bindings: analysis.components.map((f: any) => ({
          nodeId: f.nodeId,
          kind: f.kind,
          name: f.nodeId.replaceAll("-", "_"),
          mode: "static",
          label: f.label,
          motionPreset: "none",
          ...editor.bindings.find((binding: any) => binding.nodeId === f.nodeId),
        })),
      });
    });
  const saveTemplate = () =>
    action(async () => {
      await api(
        "templates" + (editor.id ? "/" + editor.id : ""),
        { ...normalizeTemplateMotion({ ...editor, slides: editor.analysis.slides, bindings: editor.bindings.map((binding: any) => binding.mode === "generated" ? { ...binding, mode: "editable" } : binding) }), projectId },
        editor.id ? "PATCH" : "POST",
      );
      setEditor(null);
      if (assetEditor) {
        forgetAssets();
        location.hash = '/assets?group=templates&project=' + encodeURIComponent(projectId);
        return;
      }
      await load();
    });
  return (
    <div className="page remotion-page">
      <div className="remotion-row">
        <div>
          <h1>{assetEditor ? 'Remotion template' : 'Remotion Templates'}</h1>
          <p className="muted">HTML layouts, cards and animated exports.</p>
        </div>
        <label>
          Project
          <select aria-label="Project" value={projectId} disabled={!!templateId} onChange={(e) => setProject(e.target.value)}>
            {projects.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        {!assetEditor && <button
          className="btn primary"
          disabled={!projectId || busy}
          onClick={newTemplate}
        >
          New HTML template
        </button>}
        {assetEditor && <a className="btn" href="#/assets?group=templates">Back to Assets</a>}
      </div>
      {error && (
        <div className="banner error" role="alert">
          {error}
        </div>
      )}
      {editor ? (
        <section className="remotion-panel">
          <div className="remotion-row">
            <h2>Template editor</h2>
            <button className="btn" onClick={() => assetEditor ? location.assign('/#/assets?group=templates') : setEditor(null)}>
              Close
            </button>
          </div>
          <label>
            Name
            <input
              value={editor.name}
              onChange={(e) => setEditor({ ...editor, name: e.target.value })}
            />
          </label>
          <label>
            Description
            <input
              value={editor.description}
              onChange={(e) =>
                setEditor({ ...editor, description: e.target.value })
              }
            />
          </label>
          <label>
            Import HTML
            <input
              type="file"
              accept=".html,text/html"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file)
                  void file.text().then((html) =>
                    setEditor({
                      ...editor,
                      html,
                      analysis: null,
                      bindings: [],
                      animations: {},
                    }),
                  );
              }}
            />
          </label>
          <label>
            HTML
            <textarea
              className="remotion-code"
              aria-label="HTML"
              value={editor.html}
              onChange={(e) =>
                setEditor({
                  ...editor,
                  html: e.target.value,
                  analysis: null,
                })
              }
            />
          </label>
          <button className="btn" disabled={busy} onClick={analyze}>
            Analyze HTML
          </button>
          {editor.analysis && editor.analysis.slides.map((slide: any) => (
            <section key={slide.index} className="remotion-card-fields" aria-label={`Card ${slide.index + 1} fields`}>
              <h3>Card {slide.index + 1} <HelpButton article="card" /></h3>
              <div className="remotion-row remotion-top">
              <Preview html={slide.html} />
              <div className="remotion-fields">
                <h3>Template fields <HelpButton article="fields" /></h3>
                {editor.analysis.components.map((field: any, i: number) => field.slideIndex === slide.index ? (
                  <div key={field.nodeId} className="remotion-binding">
                    <strong>
                      {field.label} · {field.kind}
                    </strong>
                    <span className="muted">{field.value?.slice(0, 80)}</span>
                    <select
                      aria-label={`Mode ${field.label}${editor.analysis.slides.length > 1 ? ` · Card ${field.slideIndex + 1}` : ""}`}
                      value={editor.bindings[i].mode === "generated" ? "editable" : editor.bindings[i].mode}
                      onChange={(e) =>
                        setEditor({
                          ...editor,
                          bindings: editor.bindings.map((b: any, j: number) =>
                            i === j ? { ...b, mode: e.target.value } : b,
                          ),
                        })
                      }
                    >
                      <option value="static">Static</option>
                      <option value="editable">Editable</option>
                    </select>
                    {editor.bindings[i].mode !== "static" && (
                      <input
                        aria-label={`Field name ${field.label}${editor.analysis.slides.length > 1 ? ` · Card ${field.slideIndex + 1}` : ""}`}
                        value={editor.bindings[i].name}
                        onChange={(e) =>
                          setEditor({
                            ...editor,
                            bindings: editor.bindings.map(
                              (b: any, j: number) =>
                                i === j ? { ...b, name: e.target.value } : b,
                            ),
                          })
                        }
                      />
                    )}
                  </div>
                ) : null)}
              </div>
              </div>
            </section>
          ))}
          {editor.analysis && <MotionEditor editor={editor} setEditor={setEditor} />}
          <button
            className="btn primary"
            disabled={!editor.analysis || busy}
            onClick={saveTemplate}
          >
            Save template
          </button>
        </section>
      ) : null}
      {!editor && !assetEditor && (
        <>
          <h2>Templates</h2>
          <div className="remotion-grid">
            {templates.map((t) => (
              <section className="remotion-panel" key={t.id}>
                <Preview html={t.data.slides[0]?.html || ""} />
                <h3>{t.name}</h3>
                <p className="muted">
                  {t.data.ports?.length || 0} fields · v{t.version}
                </p>
                <div className="remotion-row">
                  <button
                    className="btn"
                    onClick={() =>
                      setEditor({
                        ...normalizeTemplateMotion(t.data),
                        id: t.id,
                        analysis: {
                          slides: t.data.slides,
                          components: t.data.components,
                        },
                      })
                    }
                  >
                    Edit
                  </button>
                  <button
                    className="btn"
                    disabled={busy}
                    onClick={() =>
                      action(async () => {
                        await api("templates/" + t.id, undefined, "DELETE");
                        await load();
                      })
                    }
                  >
                    Delete
                  </button>
                </div>
              </section>
            ))}
          </div>
          {!templates.length && (
            <p>No templates yet. Import an HTML layout to start.</p>
          )}
        </>
      )}

    </div>
  );
}
export function render(context: PageContext) {
  return {
    title: "Remotion Templates",
    content: <RemotionTemplates context={context} />,
  };
}
